// File: /var/www/app2/frontend/src/components/ReportDetail.tsx

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { User } from "../App";
import "../styles/ReportDetail.css";

// Image metadata interface for pictures with descriptions
interface ImageMetadata {
  path: string;
  description: string;
}

// Uploading picture interface with preview
interface UploadingPicture {
  file: File;
  description: string;
  preview: string;
}

// Report data interface for the main report content
interface ReportData {
  heardGospel: number | string;
  receivedJesus: number | string;
  baptized: number | string;
  discoveryBibleStudyStarted: number | string;
  churchesPlanted: number | string;
  leadersTrained: number | string;
  testimony: string;
  challenges: string;
  prayerRequests: string;
  pictures: ImageMetadata[];
}

// Edit data interface extending ReportData
interface EditData extends ReportData {}

// Note interface for displaying notes
interface Note {
  id: number;
  note_text: string;
  author_name: string;
  created_at: string;
}

// Rejection reason interface
interface RejectionReason {
  reason: string;
  rejected_by_name: string;
  created_at: string;
}

// A single planted church from the field app's Church Planting Details
// prompt (village, GPS location, believers count) — historically saved but
// never surfaced here; the report only ever showed the plain count.
interface ChurchPlant {
  id: number;
  village_name: string | null;
  people_group: string | null;
  church_name: string;
  start_month: number | null;
  start_year: number | null;
  believers_count: number;
  gps_lat: number | null;
  gps_lng: number | null;
}

// Report interface for the full report object
interface Report {
  id: number;
  missionary_name: string;
  missionary_number: string;
  month: number;
  year: number;
  state: string;
  location: string;
  status: string;
  submission_date: string;
  approved_date: string | null;
  rejected_date: string | null;
  data: ReportData;
}

// Component props interface
interface ReportDetailProps {
  user: User;
}

// Route params interface
interface ReportParams extends Record<string, string | undefined> {
  id?: string;
}

function ReportDetail({ user }: ReportDetailProps): React.ReactElement {
  const { id } = useParams<ReportParams>();
  const navigate = useNavigate();
  const [report, setReport] = useState<Report | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [rejectionReasons, setRejectionReasons] = useState<RejectionReason[]>([]);
  const [churchPlants, setChurchPlants] = useState<ChurchPlant[]>([]);
  const [newNote, setNewNote] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // Edit mode states
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editData, setEditData] = useState<EditData | null>(null);
  const [editMonth, setEditMonth] = useState<number>(0);
  const [editYear, setEditYear] = useState<number>(0);
  const [saving, setSaving] = useState<boolean>(false);
  const [uploadingPictures, setUploadingPictures] = useState<UploadingPicture[]>([]);

  useEffect(() => {
    fetchReportDetail();
  }, [id]);

  // Scroll to top when viewing a new report
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const fetchReportDetail = async (): Promise<void> => {
    try {
      const response = await axios.get(`/api/reports/${id}`);
      setReport(response.data.report);
      setNotes(response.data.notes || []);
      setRejectionReasons(response.data.rejection_reasons || []);
      setChurchPlants(response.data.church_plants || []);
    } catch (error: any) {
      console.error("Failed to fetch report:", error);
      if (error?.response?.status === 404) {
        setError("Report not found.");
      } else if (error?.response?.status === 409) {
        setError("Select a country from the menu above to view this report.");
      } else {
        setError("Could not load report details. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string): Promise<void> => {
    const reason: string | null =
      newStatus === "rejected"
        ? prompt("Please provide a reason for rejection:")
        : null;

    if (newStatus === "rejected" && !reason) {
      return; // User cancelled
    }

    if (!window.confirm(`Are you sure you want to ${newStatus} this report?`)) {
      return;
    }

    try {
      await axios.patch(`/api/reports/${id}/status`, {
        status: newStatus,
        reason: reason || "",
      });

      // Refresh report data
      fetchReportDetail();
      alert(`Report ${newStatus} successfully!`);
    } catch (error: any) {
      alert(
        "Failed to update status: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  const handleRevertToReview = async (): Promise<void> => {
    if (
      !window.confirm(
        "Are you sure you want to change this report back to Review status?"
      )
    ) {
      return;
    }

    try {
      await axios.patch(`/api/reports/${id}/status`, {
        status: "review",
        reason: "Reverted to review for corrections",
      });

      fetchReportDetail();
      alert("Report status changed to Review successfully!");
    } catch (error: any) {
      alert(
        "Failed to update status: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  const handleAddNote = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      await axios.post(`/api/reports/${id}/notes`, {
        note: newNote,
      });

      setNewNote("");
      fetchReportDetail(); // Refresh to show new note
    } catch (error: any) {
      alert(
        "Failed to add note: " + (error.response?.data?.error || error.message)
      );
    }
  };

  const handleEditToggle = (): void => {
    if (!editMode) {
      // Enter edit mode
      if (report) {
        setEditData({
          ...report.data,
          pictures: processPictures(report.data),
        });
        setEditMonth(report.month);
        setEditYear(report.year);
        setEditMode(true);
      }
    } else {
      // Cancel edit mode
      if (window.confirm("Discard all changes?")) {
        setEditMode(false);
        setEditData(null);
        setEditMonth(0);
        setEditYear(0);
        setUploadingPictures([]);
      }
    }
  };

  const handleEditChange = (field: string, value: any): void => {
    if (editData) {
      setEditData({
        ...editData,
        [field]: value,
      });
    }
  };

  const handleRemovePicture = (index: number): void => {
    if (editData) {
      const newPictures = editData.pictures.filter((_, i) => i !== index);
      setEditData({
        ...editData,
        pictures: newPictures,
      });
    }
  };

  const handleAddNewPicture = (): void => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async (e: Event): Promise<void> => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file && editData) {
        const description = prompt("Enter description for this picture:");
        if (description !== null) {
          setUploadingPictures([
            ...uploadingPictures,
            {
              file: file,
              description:
                description ||
                `Picture ${
                  editData.pictures.length + uploadingPictures.length + 1
                }`,
              preview: URL.createObjectURL(file),
            },
          ]);
        }
      }
    };
    input.click();
  };

  const handleRemoveUploadingPicture = (index: number): void => {
    setUploadingPictures(uploadingPictures.filter((_, i) => i !== index));
  };

  const handleUpdatePictureDescription = (
    index: number,
    isNew: boolean,
    newDescription: string
  ): void => {
    if (isNew) {
      const newUploading = [...uploadingPictures];
      newUploading[index].description = newDescription;
      setUploadingPictures(newUploading);
    } else {
      if (editData) {
        const newPictures = [...editData.pictures];
        newPictures[index].description = newDescription;
        setEditData({
          ...editData,
          pictures: newPictures,
        });
      }
    }
  };

  const handleSaveChanges = async (): Promise<void> => {
    if (!window.confirm("Save all changes to this report?")) {
      return;
    }

    setSaving(true);
    try {
      // Prepare the update data
      if (!editData) return;

      const updateData: any = {
        ...editData,
        // Ensure numeric fields are properly formatted
        heardGospel: parseInt(String(editData.heardGospel)) || 0,
        receivedJesus: parseInt(String(editData.receivedJesus)) || 0,
        baptized: parseInt(String(editData.baptized)) || 0,
        discoveryBibleStudyStarted:
          parseInt(String(editData.discoveryBibleStudyStarted)) || 0,
        churchesPlanted: parseInt(String(editData.churchesPlanted)) || 0,
        leadersTrained: parseInt(String(editData.leadersTrained)) || 0,
        // Include month and year if changed
        month: editMonth,
        year: editYear,
      };

      // Handle new picture uploads if any
      if (uploadingPictures.length > 0) {
        // This would require implementing a picture upload endpoint
        // For now, we'll just add them to the pictures array with a placeholder
        const newPictures = uploadingPictures.map((pic, index) => ({
          path: `/uploads/pending-${Date.now()}-${index}.jpg`,
          description: pic.description,
        }));
        updateData.pictures = [...updateData.pictures, ...newPictures];
      }

      // Send update request
      await axios.put(`/api/reports/${id}/update`, updateData);

      alert("Report updated successfully!");
      setEditMode(false);
      setEditData(null);
      setEditMonth(0);
      setEditYear(0);
      setUploadingPictures([]);
      fetchReportDetail(); // Refresh the data
    } catch (error: any) {
      alert(
        "Failed to save changes: " +
          (error.response?.data?.error || error.message)
      );
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status: string): React.ReactElement => {
    return (
      <span className={`status-badge ${status}`}>{status.toUpperCase()}</span>
    );
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const months: string[] = [
    "",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Helper function to process pictures - handles both new and legacy formats
  const processPictures = (reportData: ReportData): ImageMetadata[] => {
    if (!reportData.pictures) return [];

    if (reportData.pictures.length > 0) {
      if (
        typeof reportData.pictures[0] === "object" &&
        (reportData.pictures[0] as any).path
      ) {
        return reportData.pictures;
      } else if (typeof reportData.pictures[0] === "string") {
        return (reportData.pictures as any).map((path: string, index: number) => ({
          path: path,
          description: `Picture ${index + 1}`,
        }));
      }
    }

    return [];
  };

  // Note: HEIC/HEIF images are now converted to JPEG server-side during upload
  // No client-side conversion needed anymore!

  if (loading) {
    return <div className="loading">Loading report details...</div>;
  }

  if (error || !report) {
    return (
      <div className="report-detail-container">
        <div className="report-section" style={{ textAlign: "center", padding: "40px 20px" }}>
          <p style={{ color: "#555", fontSize: 15, marginBottom: 16 }}>{error || "Report not found."}</p>
          <button onClick={() => navigate(-1)} className="back-btn">
            Back to Reports
          </button>
        </div>
      </div>
    );
  }

  const reportData = report.data || {};
  const pictures = editMode ? editData?.pictures : processPictures(reportData);

  const isAdmin = user.role === "admin" || user.role === "superadmin" || user.role === "system_admin";
  const isSupporter = user.role === "supporter";
  const canEdit =
    isAdmin && (report.status === "review" || report.status === "rejected"); // Admin can edit review and rejected
  const canRevert = isAdmin && (report.status === "approved" || report.status === "action_required" || report.status === "rejected");

  return (
    <div className="report-detail-container">
      <div className="report-header">
        <h2>
          Report Details{" "}
          {editMode && <span style={{ color: "#f56565" }}>(Edit Mode)</span>}
        </h2>
        <div style={{ display: "flex", gap: "10px" }}>
          {canEdit && !editMode && (
            <button onClick={handleEditToggle} className="edit-btn">
              Edit Report
            </button>
          )}
          {editMode && (
            <>
              <button
                onClick={handleSaveChanges}
                className="save-btn"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button onClick={handleEditToggle} className="cancel-btn">
                Cancel
              </button>
            </>
          )}
          <button onClick={() => navigate(-1)} className="back-btn">
            ← Back to Reports
          </button>
        </div>
      </div>

      {/* Report Header */}
      <div className="report-section">
        <h3>Report Information</h3>
        <div className="report-field">
          <label>Missionary:</label>
          <span>
            {report.missionary_name} ({report.missionary_number})
          </span>
        </div>
        <div className="report-field">
          <label>Period:</label>
          {editMode ? (
            <span className="period-edit">
              <select
                value={editMonth}
                onChange={(e) => setEditMonth(parseInt(e.target.value))}
                className="period-select"
              >
                {months.slice(1).map((monthName, index) => (
                  <option key={index + 1} value={index + 1}>
                    {monthName}
                  </option>
                ))}
              </select>
              <select
                value={editYear}
                onChange={(e) => setEditYear(parseInt(e.target.value))}
                className="period-select"
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </span>
          ) : (
            <span>
              {months[report.month]} {report.year}
            </span>
          )}
        </div>
        <div className="report-field">
          <label>Location:</label>
          <span>
            {report.state}, {report.location}
          </span>
        </div>
        <div className="report-field">
          <label>Status:</label>
          <span>{getStatusBadge(report.status)}</span>
        </div>
        <div className="report-field">
          <label>Submitted:</label>
          <span>{formatDate(report.submission_date)}</span>
        </div>
        {report.approved_date && (
          <div className="report-field">
            <label>Approved:</label>
            <span>{formatDate(report.approved_date)}</span>
          </div>
        )}
        {report.rejected_date && (
          <div className="report-field">
            <label>Rejected:</label>
            <span>{formatDate(report.rejected_date)}</span>
          </div>
        )}
        {rejectionReasons.length > 0 && (
          <div className="report-field">
            <label>Rejection Reason:</label>
            <span>
              {rejectionReasons.map((rr, index) => (
                <div key={index} style={{ marginBottom: index < rejectionReasons.length - 1 ? "8px" : "0" }}>
                  <span>{rr.reason}</span>
                  <span style={{ color: "#888", fontSize: "0.85em", marginLeft: "8px" }}>
                    — {rr.rejected_by_name || "Unknown"}, {formatDate(rr.created_at)}
                  </span>
                </div>
              ))}
            </span>
          </div>
        )}
      </div>

      {/* Ministry Statistics */}
      {/* Number fields below use `?? ""` (nullish coalescing), not `|| 0` --
          clearing a number input to type a new value makes it "", which is
          falsy but not null/undefined. With `|| 0` that snapped the field
          back to 0 the moment it was cleared (visible once the field lost
          focus, since number inputs don't reflect a controlled-value reset
          while still focused the same way text inputs do), making it look
          like "the number changes as soon as you move away from the field."
          `?? ""` lets a genuinely empty in-progress edit stay empty; the
          save handler already treats an empty value as 0 on submit. */}
      <div className="report-section">
        <h3>Ministry Statistics</h3>
        <div className="metrics-grid">
          <div className="metric-item">
            <div className="metric-label">Heard the Gospel</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.heardGospel ?? ""}
                onChange={(e) =>
                  handleEditChange("heardGospel", e.target.value)
                }
                min="0"
              />
            ) : (
              <div className="metric-value">{reportData.heardGospel || 0}</div>
            )}
          </div>
          <div className="metric-item">
            <div className="metric-label">Received Jesus</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.receivedJesus ?? ""}
                onChange={(e) =>
                  handleEditChange("receivedJesus", e.target.value)
                }
                min="0"
              />
            ) : (
              <div className="metric-value">
                {reportData.receivedJesus || 0}
              </div>
            )}
          </div>
          <div className="metric-item">
            <div className="metric-label">Baptized</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.baptized ?? ""}
                onChange={(e) => handleEditChange("baptized", e.target.value)}
                min="0"
              />
            ) : (
              <div className="metric-value">{reportData.baptized || 0}</div>
            )}
          </div>
          <div className="metric-item">
            <div className="metric-label">Discovery Bible Studies Started</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.discoveryBibleStudyStarted ?? ""}
                onChange={(e) =>
                  handleEditChange("discoveryBibleStudyStarted", e.target.value)
                }
                min="0"
              />
            ) : (
              <div className="metric-value">
                {reportData.discoveryBibleStudyStarted || 0}
              </div>
            )}
          </div>
          <div className="metric-item">
            <div className="metric-label">Churches Planted</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.churchesPlanted ?? ""}
                onChange={(e) =>
                  handleEditChange("churchesPlanted", e.target.value)
                }
                min="0"
              />
            ) : (
              <div className="metric-value">
                {reportData.churchesPlanted || 0}
              </div>
            )}
          </div>
          <div className="metric-item">
            <div className="metric-label">Leaders Trained</div>
            {editMode ? (
              <input
                type="number"
                className="metric-input"
                value={editData?.leadersTrained ?? ""}
                onChange={(e) =>
                  handleEditChange("leadersTrained", e.target.value)
                }
                min="0"
              />
            ) : (
              <div className="metric-value">
                {reportData.leadersTrained || 0}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Church Planting Detail — per-church breakdown behind the plain
          "Churches Planted" count above, including each church's GPS
          location captured by the field app's village picker. */}
      {churchPlants.length > 0 && (
        <div className="report-section">
          <h3>Churches Planted — Detail ({churchPlants.length})</h3>
          {churchPlants.map((c, index) => (
            <div
              key={c.id}
              style={{
                paddingBottom: 10,
                marginBottom: index < churchPlants.length - 1 ? 10 : 0,
                borderBottom: index < churchPlants.length - 1 ? "1px solid #e2e8f0" : "none",
              }}
            >
              <div style={{ fontWeight: 600 }}>{c.church_name}</div>
              <div style={{ fontSize: "0.9em", color: "#555" }}>
                {c.village_name && <div>Village: {c.village_name}</div>}
                {c.people_group && <div>People group: {c.people_group}</div>}
                <div>
                  Believers: {c.believers_count}
                  {c.start_month && c.start_year && <> — Started {months[c.start_month]} {c.start_year}</>}
                </div>
                <div style={{ fontFamily: "monospace" }}>
                  {c.gps_lat != null && c.gps_lng != null
                    ? <>Location: {c.gps_lat.toFixed(6)}, {c.gps_lng.toFixed(6)}</>
                    : <span style={{ fontFamily: "inherit", color: "#a0aec0" }}>No GPS location recorded</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Narrative Sections */}
      <div className="report-section">
        <h3>Testimony</h3>
        {editMode ? (
          <textarea
            className="narrative-textarea"
            value={editData?.testimony || ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleEditChange("testimony", e.target.value)}
            rows={6}
          />
        ) : (
          <div className="narrative-content">
            {reportData.testimony || "No testimony provided"}
          </div>
        )}
      </div>

      <div className="report-section">
        <h3>Challenges</h3>
        {editMode ? (
          <textarea
            className="narrative-textarea"
            value={editData?.challenges || ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleEditChange("challenges", e.target.value)}
            rows={6}
          />
        ) : (
          <div className="narrative-content">
            {reportData.challenges || "No challenges reported"}
          </div>
        )}
      </div>

      <div className="report-section">
        <h3>Prayer Requests</h3>
        {editMode ? (
          <textarea
            className="narrative-textarea"
            value={editData?.prayerRequests || ""}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleEditChange("prayerRequests", e.target.value)}
            rows={6}
          />
        ) : (
          <div className="narrative-content">
            {reportData.prayerRequests || "No prayer requests"}
          </div>
        )}
      </div>

      {/* Pictures with Descriptions */}
      {(pictures && pictures.length > 0) || editMode ? (
        <div className="report-section">
          <h3>
            Pictures ({pictures ? pictures.length : 0} + {uploadingPictures.length})
            {editMode && (
              <button
                onClick={handleAddNewPicture}
                className="add-picture-btn"
                style={{ marginLeft: "10px" }}
              >
                + Add Picture
              </button>
            )}
          </h3>
          <div className="pictures-grid-with-descriptions">
            {pictures && pictures.map((pic, index) => {
              const imagePath = pic.path.replace("/uploads/", "");
              return (
                <div key={index} className="picture-card">
                  <div className="picture-container">
                    <img
                      src={`/api/image-proxy/${imagePath}`}
                      alt={pic.description || `Report picture ${index + 1}`}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        const img = e.target as HTMLImageElement;
                        img.src =
                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2RkZCIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjE1MCIgeT0iMTAwIiBmaWxsPSIjOTk5Ij5JbWFnZSBub3QgYXZhaWxhYmxlPC90ZXh0Pjwvc3ZnPg==";
                        img.alt = "Image not available";
                      }}
                    />
                    {editMode && (
                      <button
                        className="remove-picture-overlay"
                        onClick={() => handleRemovePicture(index)}
                        title="Remove picture"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <div className="picture-description">
                    <span className="picture-number">Picture {index + 1}</span>
                    {editMode ? (
                      <input
                        type="text"
                        className="description-input"
                        value={pic.description || ""}
                        onChange={(e) =>
                          handleUpdatePictureDescription(
                            index,
                            false,
                            e.target.value
                          )
                        }
                        placeholder="Enter description..."
                      />
                    ) : (
                      <p>{pic.description || "No description provided"}</p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Show uploading pictures in edit mode */}
            {editMode &&
              uploadingPictures.map((pic, index) => (
                <div key={`new-${index}`} className="picture-card new-picture">
                  <div className="picture-container">
                    <img src={pic.preview} alt={`New picture ${index + 1}`} />
                    <button
                      className="remove-picture-overlay"
                      onClick={() => handleRemoveUploadingPicture(index)}
                      title="Remove picture"
                    >
                      ×
                    </button>
                  </div>
                  <div className="picture-description">
                    <span className="picture-number">New Picture</span>
                    <input
                      type="text"
                      className="description-input"
                      value={pic.description}
                      onChange={(e) =>
                        handleUpdatePictureDescription(
                          index,
                          true,
                          e.target.value
                        )
                      }
                      placeholder="Enter description..."
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : null}

      {/* Action Buttons */}
      <div className="report-section">
        <h3>Actions</h3>
        <div className="action-buttons-section">
          {/* Admin can approve reports in review (NO reject button for admin) */}
          {isAdmin && report.status === "review" && !editMode && (
            <>
              <button
                onClick={() => handleStatusChange("approved")}
                className="approve-btn"
              >
                Approve Report
              </button>
              <button
                onClick={() => handleStatusChange("action_required")}
                className="action-required-btn"
                style={{
                  backgroundColor: "#f56565",
                  color: "white",
                  marginLeft: "10px"
                }}
              >
                Action Required
              </button>
            </>
          )}

          {/* Admin can revert approved reports back to review */}
          {canRevert && !editMode && (
            <button onClick={handleRevertToReview} className="revert-btn">
              Revert to Review
            </button>
          )}


          {report.status !== "review" && !canRevert && !isSupporter && (
            <div className="status-display">Report is {report.status.replace('_', ' ')}</div>
          )}
        </div>
      </div>


      {/* Footer with Back button */}
      <div className="report-footer">
        <button onClick={() => navigate(-1)} className="back-btn">
          ← Back to Reports
        </button>
      </div>
    </div>
  );
}

export default ReportDetail;
